// The score document (§3). Pitches are spelled and written; sounding pitch is
// derived through the track's transposition. Bars are global (shared by all
// tracks); each track holds a parallel array of per-bar voice content.
//
// Everything here is plain JSON (no Sets, Maps or classes) so documents can be
// stored, diffed and sent over the wire as-is.

import type { Pitch, Duration, DurBase, Mode } from '@simplenotation/theory';

export type { Pitch, Duration, DurBase, Mode };

export const SCORE_VERSION = 1 as const;

export interface Score {
  v: typeof SCORE_VERSION;
  meta: ScoreMeta;
  bars: BarInfo[];
  tempos: TempoMark[];
  tracks: Track[];
}

export interface ScoreMeta {
  title: string;
  subtitle?: string;
  composer?: string;
  artist?: string;
  arranger?: string;
  copyright?: string;
}

export interface TempoMark {
  bar: number;
  beatOffsetTicks: number;
  bpm: number;
  beatUnit: DurBase;
  dotted?: boolean;
  text?: string;
}

export type Clef = 'treble' | 'treble8vb' | 'bass' | 'alto' | 'tenor' | 'percussion';
export type Barline = 'single' | 'double' | 'dashed' | 'final';
export type Direction = 'DC' | 'DS' | 'DC al Fine' | 'DS al Coda' | 'DC al Coda' | 'Fine' | 'Coda' | 'Segno' | 'ToCoda';

export interface TimeSignature {
  num: number;
  den: DurBase;
  symbol?: 'common' | 'cut';
}

export interface KeySignature {
  fifths: number; // -7..7
  mode: Mode;
}

export interface BarInfo {
  /** Present only where it changes (bar 0 always has one). */
  timeSig?: TimeSignature;
  keySig?: KeySignature;
  repeatStart?: boolean;
  /** Play count of an end repeat (2 = play twice). */
  repeatEnd?: number;
  /** Volta numbers this bar belongs to. */
  endings?: number[];
  direction?: Direction;
  barline?: Barline;
  pickup?: boolean;
  multiRest?: number;
  numberOverride?: number;
  section?: { name?: string; letter?: string };
  tripletFeel?: 'none' | '8th' | '16th';
  lineBreak?: 'force' | 'prevent';
  commentary?: string;
  /** Simile marks: repeat the previous 1 or 2 bars. */
  simile?: 1 | 2;
}

export type InstrumentType = string;

export type Visualization = 'piano' | 'violin' | 'trombone' | 'trumpet' | 'fretboard';

export interface Track {
  id: string;
  name: string;
  shortName?: string;
  instrument: InstrumentType;
  /** Sampler id, e.g. 'gtr_nylon'. */
  sound: string;
  /** Semitones from written to sounding pitch; guitar = −12. */
  writtenToSounding: number;
  clef: Clef;
  /** Sounding MIDI, high → low (string 1 first). Absent for non-fretted instruments. */
  tuning?: number[];
  capo?: number;
  showStaff: boolean;
  showTab: boolean;
  tabStems?: boolean;
  visualizations: Visualization[];
  volume?: number; // 0..1
  pan?: number; // -1..1
  mute?: boolean;
  solo?: boolean;
  /** Parallel to Score.bars. */
  bars: TrackBar[];
  /** Clef changes keyed by bar index (bar 0 uses Track.clef). */
  clefChanges?: Record<number, Clef>;
}

export interface TrackBar {
  /** Up to 4 voices. Voice 0 always exists. */
  voices: Beat[][];
}

export type Dynamic = 'ppp' | 'pp' | 'p' | 'mp' | 'mf' | 'f' | 'ff' | 'fff' | 'fp' | 'fz' | 'sf' | 'sfz' | 'sffz';
export const DYNAMICS: readonly Dynamic[] = ['ppp', 'pp', 'p', 'mp', 'mf', 'f', 'ff', 'fff', 'fp', 'fz', 'sf', 'sfz', 'sffz'];

export type Articulation = 'staccato' | 'staccatissimo' | 'accent' | 'marcato' | 'tenuto' | 'fermata';

export type Ornament = 'trill' | 'mordent' | 'turn';
export type JazzArtic = 'plop' | 'scoop' | 'doit' | 'fall';
export type BrassMute = 'open' | 'closed' | 'halfOpen1' | 'halfOpen2';

export interface ChordSymbol {
  name: string;
  /** Frets from string 1 (high) to n; null = not played. */
  diagram?: { frets: (number | null)[]; baseFret: number; fingers?: (number | null)[] };
}

export interface Beat {
  id: string;
  duration: Duration;
  rest: boolean;
  notes: Note[];
  grace?: 'acciaccatura' | 'appoggiatura';
  beamBreak?: 'auto' | 'join' | 'break';
  stem?: 'auto' | 'up' | 'down' | 'hidden';
  dynamics?: Dynamic;
  hairpin?: { type: 'cresc' | 'dim'; endBeatId: string };
  articulations?: Articulation[];
  ornament?: Ornament;
  jazz?: JazzArtic;
  mute?: BrassMute;
  text?: { above?: string; below?: string; innerAbove?: string; innerBelow?: string };
  chord?: ChordSymbol;
  lyrics?: string[];
  strum?: 'up' | 'down';
  arpeggio?: 'up' | 'down';
  brush?: 'up' | 'down';
  pick?: 'down' | 'up';
  tremolo?: 1 | 2 | 3;
  octaveShift?: 8 | -8 | 15 | -15;
  letRing?: boolean;
  palmMute?: boolean;
  pedal?: 'down' | 'up';
  breath?: boolean;
  sticking?: string;
  roman?: string;
  slap?: boolean;
  pop?: boolean;
  tap?: boolean;
  hidden?: boolean;
  /** Beat-level color (rest color, or all notes). */
  color?: string;
  /** Small cue-size beat. */
  small?: boolean;
  passage?: boolean;
  /** Line break after this beat's bar is a bar property; passage break is beat-level. */
  crossStaff?: 'up' | 'down';
}

export type NoteHead = 'normal' | 'x' | 'slash' | 'diamond' | 'ghost' | 'small' | 'circleX';

export type SlideType = 'legato' | 'shift' | 'inBelow' | 'inAbove' | 'outDown' | 'outUp';

export interface BendPoint {
  /** Position within the note, 0…1. */
  t: number;
  /** Bend amount in semitones. */
  semis: number;
}

export interface Note {
  id: string;
  pitch: Pitch;
  /** Tab position: 1 = highest string. */
  string?: number;
  fret?: number;
  tieToNext?: boolean;
  slurStart?: boolean;
  slurEnd?: boolean;
  slurSide?: 'auto' | 'above' | 'below';
  head?: NoteHead;
  accidentalDisplay?: 'auto' | 'cautionary' | 'hidden';
  hidden?: boolean;
  color?: string;
  // Guitar effects
  hammerPull?: boolean;
  slide?: SlideType;
  bend?: { points: BendPoint[] };
  vibrato?: boolean;
  harmonic?: 'natural' | 'artificial';
  dead?: boolean;
  tap?: boolean;
  leftHandTap?: boolean;
  pullOffToNowhere?: boolean;
  fingering?: string;
  rhFingering?: string;
  stringCircle?: boolean;
  ghost?: boolean;
}

/** Address of a beat. `beat` may equal the voice length for an insertion point in an empty voice. */
export interface BeatRef {
  track: number;
  bar: number;
  voice: number;
  beat: number;
}
